// claimRewards.ts
// Broadcasts a "withdraw rewards" transaction against the rewards contract on
// the given chain. Extracted from the (deleted) ChainSelectionModal so the
// claim flow can be triggered directly from any caller. Logic is preserved
// exactly, including the fee estimation and the setTransactionDetails shape.
import { ethers } from "ethers";
import axios from "axios";
import BigNumber from "bignumber.js";
import { getGraphqlUrl } from "../config";
import { env } from "./GlobalStateProvider";
import { getRewardsContractAddress, rewardsContractAbi } from "../old_common";

export interface ClaimRewardsDeps {
  setTransactionDetails: (details: any) => void;
  updatePointsAndRewards: () => void;
  showErrorMessage: (message: string) => void;
  openConnectModal?: () => void;
}

export const broadcastClaimRewardsTx = async (
  walletClient: any,
  currentChainId: number,
  deps: ClaimRewardsDeps,
) => {
  const {
    setTransactionDetails,
    updatePointsAndRewards,
    showErrorMessage,
    openConnectModal,
  } = deps;

  if (!walletClient) {
    openConnectModal?.();
    if (process.env.NODE_ENV === 'development') console.log("No wallet connected");
    return;
  }
  const provider = new ethers.BrowserProvider(walletClient);
  const rewardsContractAddress = getRewardsContractAddress(currentChainId);
  const signer = await provider.getSigner();
  const contract = new ethers.Contract(rewardsContractAddress, rewardsContractAbi, signer);
  const address = await signer.getAddress();
  const result = await contract.userRewards(address);

  // const totalRewardsEarned = ethers.formatEther(result[0].toString())
  // const totalRewardsWithdrawn = ethers.formatEther(result[1].toString())
  const totalAvailableForWithdraw = (result[0] - result[1]).toString()

  let tx

  try {
    tx = await contract.withdrawRewards(totalAvailableForWithdraw);
  } catch(e) {
    return showErrorMessage(`Tx Error: user rejected action`);
  }
  if (process.env.NODE_ENV === 'development') console.log("Transaction sent:", tx.hash);

  setTimeout(async () => {
    if (process.env.NODE_ENV === 'development') console.log('updating points and rewards')
    updatePointsAndRewards()
  }, 10000)

  const amount = totalAvailableForWithdraw


  // MOR + ETH prices via the public GraphQL endpoint (no API key, no
  // dependency on the /v3 Referer bypass). One request returns both.
  let usdPerMor: number = 15 // fallback (legacy hardcoded value — revisit)
  let usdPerEth: number = 3000 // fallback — rough baseline so the claim flow still completes
  try {
    const priceRes = await axios.post(
      getGraphqlUrl(env),
      {
        query: `{
          m: asset(id: "morpheus") { priceUsd }
          e: asset(id: "ethereum") { priceUsd }
        }`,
      },
      { headers: { "Content-Type": "application/json" } }
    );
    const morUsd = parseFloat(priceRes.data?.data?.m?.priceUsd);
    const ethUsd = parseFloat(priceRes.data?.data?.e?.priceUsd);
    // Fall back per-asset so a single missing price doesn't discard the other.
    if (Number.isFinite(morUsd) && morUsd > 0) {
      usdPerMor = morUsd;
    } else {
      if (process.env.NODE_ENV === 'development') console.log('MOR price missing from GraphQL response, using fallback')
      usdPerMor = 15 // fallback (legacy hardcoded value — revisit)
    }
    if (Number.isFinite(ethUsd) && ethUsd > 0) {
      usdPerEth = ethUsd;
    } else {
      if (process.env.NODE_ENV === 'development') console.log('ETH price missing from GraphQL response, using fallback')
      usdPerEth = 3000 // fallback — rough baseline so the claim flow still completes
    }
  } catch(e) {
    // Network/request error: use fallbacks rather than break the claim flow.
    if (process.env.NODE_ENV === 'development') console.log('error getting MOR/ETH price, using fallbacks', e)
    usdPerMor = 15 // fallback (legacy hardcoded value — revisit)
    usdPerEth = 3000 // fallback — rough baseline so the claim flow still completes
  }



  const feeData = await provider.getFeeData();
  const gasPrice = feeData.gasPrice;
  const gasPriceGwei = parseFloat(ethers.formatUnits(gasPrice ?? "0", "gwei"))


  const estimatedFeeEth = new BigNumber(gasPriceGwei)
    .multipliedBy(200000)
    .div(1e9)
    .toNumber();

    const morAmount = new BigNumber(amount).toNumber()
    const morPrice = new BigNumber(usdPerMor).toNumber();
    const ethPrice = new BigNumber(usdPerEth).toNumber();
    const ethFee = new BigNumber(estimatedFeeEth).toNumber();


  setTransactionDetails({
    tx: tx.hash,
    morAmount,
    morPrice,
    ethFee,
    ethPrice,
  });

  return tx.hash;
};
